import {HttpClient, HttpHeaders} from '@angular/common/http';
import {Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Router, RouterModule} from '@angular/router';
import {CommonModule} from '@angular/common';
import {ConfigService} from '@shared-library';
import {NGXLogger} from 'ngx-logger';
import {Subscription} from 'rxjs';
import {environment} from '../../../environments/environment';
import {
  FASTEN_CONNECT_OAUTH_CONTEXT_CACHE_KEY_PREFIX, getOAuthCallbackContext, OAuthCallbackContext,
  restoreConfigurationFromOauthContext,
} from "../../utils/oauth2_callback_context";
import {
  FdkIllustrationComponent,
  FdkLoadingDotsComponent,
  FdkButtonComponent,
  FdkSpinnerComponent,
} from 'fasten-design-kit';

type CallbackStatus = 'processing' | 'error';

@Component({
  selector: 'app-auth-callback',
  templateUrl: './auth-callback.component.html',
  styleUrls: ['./auth-callback.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FdkSpinnerComponent,
    FdkIllustrationComponent,
    FdkLoadingDotsComponent,
    FdkButtonComponent,
  ],
})
export class AuthCallbackComponent implements OnInit, OnDestroy {
  status: CallbackStatus = 'processing';
  error = '';
  errorDescription = '';
  errorUri = '';

  private routeSubscription?: Subscription;

  constructor(
    private httpClient: HttpClient,
    private configService: ConfigService,
    private route: ActivatedRoute,
    private router: Router,
    private logger: NGXLogger,
  ) {}

  ngOnInit(): void {
    this.routeSubscription = this.route.queryParamMap.subscribe((queryParams) => {
      this.handleCallbackParams({
        code: queryParams.get('code') || '',
        state: queryParams.get('state') || '',
        error: queryParams.get('error') || '',
        errorUri: queryParams.get('error_uri') || '',
        errorDescription: queryParams.get('error_description') || '',
      });
    });
  }

  ngOnDestroy(): void {
    if (this.routeSubscription) {
      this.routeSubscription.unsubscribe();
    }
  }

  handleCallbackParams(params: {
    code: string;
    state: string;
    error: string;
    errorUri: string;
    errorDescription: string;
  }): void {
    if (params.error) {
      this.showError(
        params.error,
        params.errorDescription || 'The identity provider returned an error before sign-in could finish.',
        params.errorUri,
      );
      return;
    }

    if (!params.code || !params.state) {
      this.showError(
        'missing_callback_parameters',
        'The sign-in callback was missing required parameters. Please try signing in again.',
      );
      return;
    }

    const oauthContext = getOAuthCallbackContext(params.state, this.logger);
    if (!oauthContext.codeVerifier) {
      this.showError(
        'missing_code_verifier',
        'Your secure sign-in session could not be found. Please return to sign in and try again.',
      );
      return;
    }

    restoreConfigurationFromOauthContext(oauthContext, this.configService);

    this.exchangeCodeForCookie(params.code, params.state, oauthContext);
  }

  private exchangeCodeForCookie(code: string, state: string, oauthContext: OAuthCallbackContext): void {
    this.status = 'processing';
    this.error = '';
    this.errorDescription = '';
    this.errorUri = '';

    const formBody = new URLSearchParams();
    formBody.set('grant_type', 'authorization_code');
    formBody.set('client_id', this.configService.systemConfig$.publicId);
    formBody.set('code', code);
    formBody.set('code_verifier', oauthContext.codeVerifier);
    formBody.set('redirect_uri', this.getCallbackUrl());
    formBody.set('response_mode', 'cookie');

    this.httpClient.post(
      `${environment.identity_api_endpoint_base}/oauth2/token`,
      formBody.toString(),
      {
        headers: new HttpHeaders({'Content-Type': 'application/x-www-form-urlencoded'}),
        withCredentials: true,
      },
    ).subscribe({
      next: async () => {
        localStorage.removeItem(`${FASTEN_CONNECT_OAUTH_CONTEXT_CACHE_KEY_PREFIX}${state}`);
        await this.router.navigateByUrl('/dashboard', {replaceUrl: true});
      },
      error: (err) => {
        this.logger.error('OAuth token exchange failed', err);
        this.showError(
          'token_exchange_failed',
          'We could not finish signing you in. Please return to sign in and try again.',
        );
      },
    });
  }

  private showError(error: string, errorDescription: string, errorUri = ''): void {
    this.status = 'error';
    this.error = error;
    this.errorDescription = errorDescription;
    this.errorUri = errorUri;
  }

  private getCallbackUrl(): string {
    if (environment.connect_base_domain === 'localhost') {
      return `${window.location.origin}/auth/callback`;
    }

    return `https://embed.${environment.connect_base_domain}/auth/callback`;
  }
}
